import { useCallback } from 'react';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';
import { useQueryClient } from '@tanstack/react-query';
// mantine
import { ActionIcon, MultiSelect, TextInput } from '@mantine/core';
// icons
import { ArrowDownToLine, Ban, RefreshCcw } from 'lucide-react';
// components
import { ILevel, ILogType, TPersona, IActor } from '@newo/api/gateway';
import PersonasAutocomplete from '@/modules/Personas/components/PersonasAutocomplete';
import UserSelector from '@/modules/Analytics/components/Toolbar/components/UserSelector';
import EditableDateTimePicker from '@/components/EditableDateTimePicker';
// utils
import { ILogsFilter } from '@/modules/Analytics/Analytics.types.ts';
import useAnalyticsStore from '@/modules/Analytics/Analytics.store.ts';
// assets
import styles from '@/modules/Analytics/components/Toolbar/Toolbar.module.css';
import { isValidUUID } from '@/utils';

interface ToolbarProps {
    filters: ILogsFilter;
}

const isValid = (uuid?: string) => {
    if (!uuid) {
        return true;
    }
    return isValidUUID(uuid);
};

const Toolbar = ({ filters }: ToolbarProps) => {
    const queryClient = useQueryClient();
    const { setFilters, foldAnalytics, clearFilters, isLoading } =
        useAnalyticsStore(
            useShallow((state) => ({
                setFilters: state.setFilters,
                foldAnalytics: state.foldAnalytics,
                clearFilters: state.clearFilters,
                isLoading: state.isLoading
            }))
        );

    const handleLevelChange = useCallback(
        (value: string[]) => {
            setFilters({ levels: value as ILevel[] });
        },
        [setFilters]
    );

    const handleTypeChange = useCallback(
        (value: string[]) => {
            setFilters({ log_types: value as ILogType[] });
        },
        [setFilters]
    );

    const handleAgentSelect = useCallback(
        (agentPersona?: TPersona) => {
            setFilters({ agentPersona });
        },
        [setFilters]
    );

    const handleUserSelect = useCallback(
        (userPersona?: TPersona, userActor?: IActor) => {
            setFilters({ userPersona, userActor });
        },
        [setFilters]
    );

    const handleFromDateChange = useCallback(
        (value: string | undefined) => {
            setFilters({ from_datetime: value });
        },
        [setFilters]
    );

    const handleToDateChange = useCallback(
        (value: string | undefined) => {
            setFilters({ to_datetime: value });
        },
        [setFilters]
    );

    const handleRefresh = () => {
        queryClient.invalidateQueries({
            queryKey: ['analytics/logs/paginated']
        });
    };

    return (
        <div className={styles.toolbar}>
            <div className={styles.top}>
                <span>Logs</span>

                <ActionIcon
                    color="blue"
                    loading={isLoading}
                    size="xs"
                    onClick={handleRefresh}
                >
                    <RefreshCcw />
                </ActionIcon>

                <ActionIcon
                    className="mr-16"
                    color="blue"
                    size="xs"
                    onClick={clearFilters}
                >
                    <Ban />
                </ActionIcon>

                <ActionIcon
                    className="ml-auto"
                    color="blue"
                    size="xs"
                    onClick={foldAnalytics}
                >
                    <ArrowDownToLine />
                </ActionIcon>
            </div>

            <div className={styles.content}>
                <MultiSelect
                    className={clsx({
                        [styles.hideInput]: !!filters.levels?.length
                    })}
                    data={[
                        { value: 'info', label: 'Info' },
                        { value: 'warning', label: 'Warning' },
                        { value: 'error', label: 'Error' }
                    ]}
                    label="Log Level"
                    placeholder="Filter by level"
                    value={filters.levels}
                    onChange={handleLevelChange}
                />
                <MultiSelect
                    className={clsx({
                        [styles.hideInput]: !!filters.log_types?.length
                    })}
                    data={[
                        { value: 'system', label: 'System' },
                        { value: 'operation', label: 'Operation' },
                        { value: 'call', label: 'Call' }
                    ]}
                    label="Log Type"
                    placeholder="Filter by type"
                    value={filters.log_types}
                    onChange={handleTypeChange}
                />
                <TextInput
                    label="Search by message"
                    placeholder="Search..."
                    value={filters.message}
                    onChange={(event) => {
                        setFilters({ message: event.target.value });
                    }}
                />
                <TextInput
                    label="Search by project Idn"
                    placeholder="default_project"
                    value={filters.project_idn}
                    onChange={(event) => {
                        setFilters({ project_idn: event.target.value });
                    }}
                />
                <TextInput
                    label="Search by flow Idn"
                    placeholder="GMmainFlow"
                    value={filters.flow_idn}
                    onChange={(event) => {
                        setFilters({ flow_idn: event.target.value });
                    }}
                />
                <TextInput
                    label="Search by skill Idn"
                    placeholder="ConvoSkill"
                    value={filters.skill_idn}
                    onChange={(event) => {
                        setFilters({ skill_idn: event.target.value });
                    }}
                />
                <TextInput
                    error={!isValid(filters.runtime_context_id)}
                    label="Runtime Context Id"
                    placeholder="7d0b9cf7-5dbc-429f-b4d8-a484d1cf46e5"
                    value={filters.runtime_context_id}
                    onChange={(event) => {
                        setFilters({
                            runtime_context_id: event.target.value || undefined
                        });
                    }}
                />

                <EditableDateTimePicker
                    label="From date"
                    placeholder="From date"
                    value={filters.from_datetime}
                    onChange={handleFromDateChange}
                />
                <EditableDateTimePicker
                    label="To date"
                    placeholder="To date"
                    value={filters.to_datetime}
                    onChange={handleToDateChange}
                />
                <TextInput
                    error={!isValid(filters.external_event_id)}
                    label="External Event Id"
                    value={filters.external_event_id}
                    onChange={(event) => {
                        setFilters({ external_event_id: event.target.value });
                    }}
                />
                <PersonasAutocomplete
                    label="Agent"
                    params={{ is_linked_to_agent: true }}
                    personaName={filters.agentPersona?.name}
                    placeholder="Start typing..."
                    onSelect={handleAgentSelect}
                />
                <UserSelector
                    actor={filters.userActor}
                    persona={filters.userPersona}
                    onSelect={handleUserSelect}
                />
            </div>
        </div>
    );
};

export default Toolbar;
